import dayjs from "dayjs";

import { dateOf, minutesOf } from "../utils/sessionTime";
import {
  HOUR_HEIGHT,
  MIN_BLOCK_MINUTES,
  NO_LOCATION_COLOR,
  NO_LOCATION_KEY,
  NO_LOCATION_NAME,
} from "./constants";

const MINUTES_IN_DAY = 24 * 60;

export const formatMinutes = (minutes) =>
  `${String(Math.floor(minutes / 60) % 24).padStart(2, "0")}:${String(
    minutes % 60
  ).padStart(2, "0")}`;

export const formatHour = (hour) => formatMinutes(hour * 60);

export const minutesNow = () => dayjs().hour() * 60 + dayjs().minute();

/** Pixels a span of minutes takes up in the grid. */
export const minutesToOffset = (minutes) => (minutes / 60) * HOUR_HEIGHT;

/** Which column a session belongs to. Always a string, so it can be stored. */
export const columnKeyOf = (session) =>
  String(session.location?.id ?? NO_LOCATION_KEY);

/**
 * Does the session occupy any time on the given day? Covers sessions
 * starting on the day and ones started earlier that run past midnight.
 */
export function sessionOccursOnDay(session, day) {
  if (dateOf(session.starting) === day) return true;
  if (dateOf(session.starting) > day) return false;
  const endDay = dateOf(session.ending);
  return endDay > day || (endDay === day && minutesOf(session.ending) > 0);
}

/**
 * The session's minute range within the given day: continuations of an
 * earlier day start at 00:00, runs past midnight are clamped to 24:00, and
 * every block is at least MIN_BLOCK_MINUTES tall so it stays clickable.
 */
export function blockRange(session, day) {
  const startMin =
    dateOf(session.starting) < day ? 0 : minutesOf(session.starting);
  const rawEndMin =
    dateOf(session.ending) > day ? MINUTES_IN_DAY : minutesOf(session.ending);
  const endMin = Math.min(
    MINUTES_IN_DAY,
    Math.max(rawEndMin, startMin + MIN_BLOCK_MINUTES)
  );
  return { startMin, endMin };
}

/**
 * Assigns overlapping sessions of one location to side-by-side lanes.
 * Returns layout objects: { session, startMin, endMin, lane, laneCount }.
 */
export function layoutSessions(sessions, day) {
  const blocks = sessions
    .map((session) => ({
      session,
      ...blockRange(session, day),
      lane: 0,
      laneCount: 1,
    }))
    .sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin);

  const laneEnds = [];
  let cluster = [];
  let clusterEnd = -1;

  const flushCluster = () => {
    const laneCount = laneEnds.length;
    cluster.forEach((block) => {
      block.laneCount = laneCount;
    });
    cluster = [];
    laneEnds.length = 0;
  };

  blocks.forEach((block) => {
    if (cluster.length > 0 && block.startMin >= clusterEnd) {
      flushCluster();
    }

    let lane = laneEnds.findIndex((end) => end <= block.startMin);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(block.endMin);
    } else {
      laneEnds[lane] = block.endMin;
    }

    block.lane = lane;
    cluster.push(block);
    clusterEnd = Math.max(clusterEnd, block.endMin);
  });

  if (cluster.length > 0) {
    flushCluster();
  }

  return blocks;
}

/**
 * Busiest location first — the count each header shows for the open day — then
 * alphabetically. The catch-all "No location" column stays last however many
 * sessions land in it.
 */
const compareColumns = (a, b) => {
  if (a.key === NO_LOCATION_KEY) return 1;
  if (b.key === NO_LOCATION_KEY) return -1;
  return (
    b.sessions.length - a.sessions.length ||
    String(a.name).localeCompare(String(b.name))
  );
};

/** One column per location that has sessions on the day, in display order. */
export function buildColumns({ daySessions, locationColors }) {
  const byKey = new Map();

  daySessions.forEach((session) => {
    const key = columnKeyOf(session);
    if (!byKey.has(key)) {
      byKey.set(key, {
        key,
        name: session.location?.name ?? NO_LOCATION_NAME,
        color: locationColors.get(key) ?? NO_LOCATION_COLOR,
        sessions: [],
      });
    }
    byKey.get(key).sessions.push(session);
  });

  return [...byKey.values()].sort(compareColumns);
}

/**
 * The hours the grid spans: every session on the day, plus the current hour
 * when `nowMinutes` is given (today), so the "now" line always has a place to
 * land. Empty days fall back to a plain working day.
 */
export function hourWindow({ daySessions, day, nowMinutes }) {
  let minStart = 8 * 60;
  let maxEnd = 18 * 60;

  if (daySessions.length > 0) {
    minStart = MINUTES_IN_DAY;
    maxEnd = 0;
    daySessions.forEach((session) => {
      // Same clamps as the rendered blocks so nothing overflows the grid
      const { startMin, endMin } = blockRange(session, day);
      minStart = Math.min(minStart, startMin);
      maxEnd = Math.max(maxEnd, endMin);
    });
  }

  let startHour = Math.floor(minStart / 60);
  let endHour = Math.min(24, Math.ceil(maxEnd / 60));

  if (nowMinutes !== null) {
    const nowHour = Math.floor(nowMinutes / 60);
    startHour = Math.min(startHour, nowHour);
    endHour = Math.max(endHour, Math.min(24, nowHour + 1));
  }

  return { startHour, endHour: Math.max(endHour, Math.min(24, startHour + 1)) };
}
